import { ChevronRight, LogOut, Settings } from 'lucide-react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useClerk } from '@clerk/clerk-react'
import { signOutWithMedia } from '@/app/useMediaSession'
import { Avatar } from '@/components/ui/Avatar'
import { IconButton } from '@/components/ui/IconButton'
import { Tooltip } from '@/components/ui/Tooltip'
import { useActiveChannel } from '@/hooks/useActiveChannel'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { forwardChannel } from '@/lib/routing'
import styles from './Topbar.module.scss'
import { projectsLinkSearch } from '@/features/projects/filters'

export interface BreadcrumbItem {
  label: string
  to?: string
}

export interface TopbarProps {
  /** Current route match id — used to derive breadcrumbs. */
  matchId: string
}

export function Topbar({ matchId }: TopbarProps) {
  const search = useSearch({ strict: false })
  const channelSlug =
    'channel' in search && typeof search.channel === 'string' ? search.channel : null
  const { active } = useActiveChannel(channelSlug)
  const channelName = active?.name ?? null
  const navigate = useNavigate()
  const { signOut } = useClerk()
  const currentUser = useCurrentUser()

  const crumbs = buildCrumbs(matchId, channelName)

  return (
    <header className={styles.topbar}>
      <nav aria-label="Breadcrumb" className={styles.crumbs}>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1
          return (
            <span key={`${c.label}-${i.toString()}`} className={styles.crumbWrap}>
              {i > 0 ? <ChevronRight aria-hidden className={styles.sep} /> : null}
              {c.to && !isLast ? (
                <Link
                  to={c.to}
                  search={c.to === '/' ? projectsLinkSearch(channelSlug) : forwardChannel}
                  className={styles.crumbLink}
                >
                  {c.label}
                </Link>
              ) : (
                <span className={isLast ? styles.crumbCurrent : styles.crumbLink}>{c.label}</span>
              )}
            </span>
          )
        })}
      </nav>

      <div className={styles.spacer} />

      <Tooltip content="Configurações">
        <IconButton
          aria-label="Configurações"
          variant="outline"
          onClick={() => {
            void navigate({ to: '/settings', search: forwardChannel })
          }}
        >
          <Settings />
        </IconButton>
      </Tooltip>
      <Avatar name={currentUser.name} src={currentUser.imageUrl ?? undefined} />
      <Tooltip content="Sair">
        <IconButton
          aria-label="Sair"
          variant="outline"
          onClick={() => {
            // Don't pass a callback — App.tsx watches `isSignedIn` and
            // invalidates the router, which re-runs the appShell guard
            // and redirects to /login. Calling navigate here too would
            // race with that and could land on a stale URL. The media cookie
            // goes first (card #277): Clerk reloads the page.
            void signOutWithMedia(signOut)
          }}
        >
          <LogOut />
        </IconButton>
      </Tooltip>
    </header>
  )
}

/**
 * Hierarchical breadcrumbs built from the match id. Static labels for
 * known routes; dynamic pieces (video title) land in later deliveries.
 */
function buildCrumbs(matchId: string, channelName: string | null): BreadcrumbItem[] {
  const base: BreadcrumbItem[] = []
  // Routes live under the pathless `_app` layout route, so match ids look
  // like `/_app/jobs`. Strip the prefix so the conditions below stay
  // expressed in URL terms.
  matchId = matchId.replace(/^\/_app/, '') || '/'

  if (matchId.startsWith('/projects/')) {
    base.push({ label: 'Projects', to: '/' })
    base.push({ label: 'Projeto' })
  } else if (matchId === '/factory' || matchId.startsWith('/factory/')) {
    base.push({ label: 'Factories', to: '/factory' })
    const leaf = FACTORY_LEAF_LABEL[matchId]
    if (leaf) base.push({ label: leaf })
  } else if (matchId === '/jobs') {
    base.push({ label: 'Jobs' })
  } else if (matchId === '/scheduler') {
    base.push({ label: 'Scheduler' })
  } else if (matchId === '/safety') {
    base.push({ label: 'Safety' })
  } else if (matchId === '/ranking') {
    base.push({ label: 'Ranking' })
  } else if (matchId === '/analytics') {
    base.push({ label: 'Analytics' })
  } else if (matchId === '/reports') {
    base.push({ label: 'Reports' })
  } else if (matchId === '/settings') {
    base.push({ label: 'Configurações' })
  } else if (matchId === '/channels') {
    base.push({ label: 'Canais' })
  } else if (matchId === '/tools' || matchId.startsWith('/tools/')) {
    base.push({ label: 'Tools' })
  } else if (matchId.startsWith('/internal/design-system')) {
    base.push({ label: 'Design System' })
  } else {
    base.push({ label: 'Projects' })
  }

  const last = base[base.length - 1]
  if (channelName && last) {
    // Channel is a sibling context, not a parent — append with a separator dot.
    base[base.length - 1] = {
      ...last,
      label: `${last.label} · ${channelName}`,
    }
  }

  return base
}

const FACTORY_LEAF_LABEL: Record<string, string> = {
  '/factory/viral-shorts': 'Viral',
  '/factory/atlas': 'Atlas',
  '/factory/mirage': 'Mirage',
  '/factory/pov-lives': 'POV Lives',
  '/factory/product-review': 'Review de produto',
  '/factory/football': 'Futebol',
  '/factory/nasa-apod': 'APOD · NASA',
  '/factory/quiz': 'Quiz',
  '/factory/reddit-stories': 'Reddit Stories',
  '/factory/etymology': 'Etimologia',
  '/factory/ranked': 'Ranked',
  '/factory/code-levels': 'Code Levels',
}
